/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import { Feed } from '../../types/read-feed.types';
import { addToMyList, getCustomerDetails, removeItemFromList } from '../hooks/use-my-list';
import { DirectusCustomerDetails } from '../hooks/use-my-list';
import { useAuth } from './AuthContext';
import { getTranslation } from '../../utils/directus-translation';
import { postAnalyticsEvent } from '../api/send-data';
import { htmlToPlainText, parseTimestamp, buildUrl } from '../../utils/helper';

export enum SortBy {
  AZ = 'alphabetical',
  POP = 'popular',
}

export interface SavedPostData {
  id?: string;
  posts_id?: {
    id?: string;
    title?: string;
    channel?: {
      image?: string;
      title?: string;
      id?: string;
      category?: { name?: string };
    };
    content?: string;
    published_at?: string;
    description?: string;
    image?: string;
    media_type?: { title?: string };
    translations?: Array<{ languages_code: { code: string }; title: string; content: string }>;
  };
}

/**
 * BFF saved post structure - flat structure returned from BFF endpoints
 */
export interface BFFSavedPost {
  linkId: string | number;
  savedAt: string;
  id: string;
  title: string;
  description: string | null;
  content: string;
  coverImage: string;
  publishedAt: string | null;
  channelImageUri: string;
  channelName: string;
  channel: number;
  translations: unknown[];
  isRecommendation: boolean;
  dateCreated: string;
  userUpdated: string;
  mediaType: string;
  video?: string;
  category?: string;
  formattedText?: string;
}

export interface MyListContextValue {
  isInList: (postId?: string) => boolean;
  manageMyList: (post: Feed) => Promise<void>;
  error: string | null;
  setFilter: (categories: string[]) => void;
  toggleSort: (sortBy?: SortBy) => void;
  refetchList: () => Promise<void>;
  setIsChannel: (isChannel: boolean) => void;
  myList: Feed[];
  setScreen: (selectedView: string) => void;
}

const MyListContext = createContext<MyListContextValue | undefined>(undefined);

export const MyListProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { customerDetails, setCustomerId } = useAuth();
  const { msisdn, opco, language } = customerDetails;
  const [myList, setMyList] = useState<Feed[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [isChannel, setIsChannel] = useState<boolean>(false);
  const [screenValue, setScreenValue] = useState<string>('');

  const setIsChannelCallback = useCallback(
    (isChannelValue: boolean) => {
      setIsChannel(isChannelValue);
      console.trace(isChannel);
    },
    [isChannel]
  );

  const setScreenCallback = useCallback(
    (selectedView: string) => {
      setScreenValue(selectedView ?? '');
    },
    [setScreenValue]
  );

  const getUniquePosts = useCallback(
    (savedPosts: Array<unknown>): Feed[] => {
      if (!savedPosts || savedPosts.length === 0) {
        return [];
      }

      const filteredPosts = savedPosts.filter((post: unknown) => {
        const postData = post as BFFSavedPost;
        const isReadContent = postData?.mediaType === 'Text';
        return isReadContent;
      });

      const mappedPosts = filteredPosts
        .map((post: unknown) => {
          // Handle flat BFF structure
          const postData = post as BFFSavedPost;

          // Cast postData to compatible type for getTranslation
          // getTranslation expects an item with translations, title, content, description
          const translationItem = postData as unknown as {
            translations: Array<{
              languages_code: { code: string };
              title: string;
              description: string;
              content: string;
            }>;
            title: string;
            content: string;
            description: string;
          };

          return {
            id: postData.id ?? '',
            linkId: postData.linkId ?? postData.id,
            title: language
              ? getTranslation(translationItem, 'title', language)
              : (postData.title ?? ''),
            channelImageUri: buildUrl(postData.channelImageUri),
            channelName: postData.channelName ?? '',
            formattedText: language
              ? getTranslation(translationItem, 'content', language)
              : (postData.formattedText ?? postData.content ?? ''),
            publishedAt: postData.publishedAt ?? undefined,
            description: language
              ? getTranslation(translationItem, 'description', language)
              : (postData.description ?? ''),
            content: language
              ? getTranslation(translationItem, 'content', language)
              : (postData.content ?? ''),
            coverImage: buildUrl(postData.coverImage),
            category: postData.category,
            channel:
              typeof postData.channel === 'number'
                ? postData.channel
                : parseInt(String(postData.channel ?? '0'), 10),
            opco: opco,
            dateCreated: postData.dateCreated ?? '',
            userUpdated: postData.userUpdated ?? '',
            isRecommendation: postData.isRecommendation ?? false,
          } as Feed;
        })
        .filter((post): post is Feed => {
          return post !== null && post !== undefined;
        });

      // Deduplicate by post id - keep first occurrence
      const seenIds = new Set<string>();
      const uniquePosts = mappedPosts.filter((post) => {
        if (!post || seenIds.has(post.id)) {
          return false; // Duplicate found or null, skip it
        }
        seenIds.add(post.id);
        return true;
      });

      return uniquePosts;
    },
    [language, opco]
  );

  const fetchCustomerDetails = useCallback(async (): Promise<void> => {
    try {
      const customerDetailsData: DirectusCustomerDetails | undefined = await getCustomerDetails(
        msisdn,
        filterCategories
      );

      if (!customerDetailsData) {
        setError('Customer not found');
        return;
      }

      setCustomerId(customerDetailsData.id);

      if (!customerDetailsData.savedPosts || customerDetailsData.savedPosts.length === 0) {
        // Don't clear the list if there are no saved posts - keep existing items
        return;
      }

      const postsToProcess = customerDetailsData.saved_posts ?? customerDetailsData.savedPosts;

      if (!postsToProcess || !Array.isArray(postsToProcess) || postsToProcess.length === 0) {
        setMyList([]);
        return;
      }

      const savedPosts = postsToProcess.map((item: unknown) => ({
        ...(item as Record<string, unknown>),
      }));
      const uniquePosts = getUniquePosts(savedPosts);
      // Always replace the list, don't append - this prevents duplicates
      setMyList(uniquePosts);
    } catch (manageError) {
      setError(manageError instanceof Error ? manageError.message : 'An error occurred');
    }
  }, [msisdn, filterCategories, setCustomerId, getUniquePosts]);

  useEffect(() => {
    if (msisdn && customerDetails.id === '') {
      fetchCustomerDetails().catch((err) => {
        setError('Error fetching customer details');
        console.error('Error fetching customer details:', err);
      });
    }
  }, [msisdn, customerDetails.id, fetchCustomerDetails]);

  const isPostInMyList = useCallback(
    (postId?: string): boolean => {
      return myList.some((post) => post.id === postId);
    },
    [myList]
  );

  const manageMyList = useCallback(
    async (post: Feed) => {
      if (!customerDetails.id) return;
      try {
        if (!isPostInMyList(post.id)) {
          const response = await addToMyList(post.id, customerDetails.msisdn);
          if (response.success) {
            setMyList((prevList) => [...prevList, { ...post, linkId: response.id }]);
          }
          try {
            await postAnalyticsEvent({
              eventName: 'click_read_add_list',
              entryPoint: 'Content',
              vertical: 'Digital Content - Read & Post Articles',
              eventParams: {
                screen: screenValue,
                content: {
                  article_id: post.id,
                  article_title: post.title,
                  article_description: htmlToPlainText(
                    post.formattedText ?? post.content ?? post.description ?? ''
                  ),
                  article_publish_timestamp: parseTimestamp(
                    isChannel ? post.dateCreated : post.publishedAt
                  ),
                  article_publisher: isChannel
                    ? typeof post.userUpdated === 'string'
                      ? post.userUpdated
                      : ''
                    : typeof post.channelName === 'string'
                      ? post.channelName
                      : '',
                  is_sponsored: !!post.isSponsored,
                  is_recommendation: !!post.isRecommendation,
                  sponsored_by: post.sponsoredBy?.toString() ?? '',
                },
                response: {
                  status: 'success',
                  message: 'successfully added to my list',
                },
              },
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (errors: any) {
            await postAnalyticsEvent({
              eventName: 'click_read_add_list',
              entryPoint: 'Content',
              vertical: 'Digital Content - Read & Post Articles',
              eventParams: {
                screen: screenValue,
                content: {
                  article_id: post.id,
                  article_title: post.title,
                  article_description: htmlToPlainText(
                    post.formattedText ?? post.content ?? post.description ?? ''
                  ),
                  article_publish_timestamp: parseTimestamp(
                    isChannel ? post.dateCreated : post.publishedAt
                  ),
                  article_publisher: isChannel
                    ? typeof post.userUpdated === 'string'
                      ? post.userUpdated
                      : ''
                    : typeof post.channelName === 'string'
                      ? post.channelName
                      : '',
                  is_sponsored: !!post.isSponsored,
                  is_recommendation: !!post.isRecommendation,
                  sponsored_by: post.sponsoredBy?.toString() ?? '',
                },
                response: {
                  status: 'fail',
                  message: errors?.message ?? 'Error posting analytics event',
                },
              },
            });
            console.error('Add List : Error posting analytics event:', errors);
          }
        } else {
          const itemToRemove = myList.find((item) => item.id === post.id);
          if (
            itemToRemove?.linkId &&
            (typeof itemToRemove.linkId === 'string' || typeof itemToRemove.linkId === 'number')
          ) {
            await removeItemFromList(itemToRemove.linkId, itemToRemove.id, customerDetails.msisdn);
            setMyList((prevList) => prevList.filter((item) => item.id !== post.id));
          }
          try {
            await postAnalyticsEvent({
              eventName: 'click_read_remove_list',
              entryPoint: 'Content',
              vertical: 'Digital Content - Read & Post Articles',
              eventParams: {
                screen: screenValue,
                content: {
                  article_id: post.id,
                  article_title: post.title,
                  article_description: htmlToPlainText(
                    post.formattedText ?? post.content ?? post.description ?? ''
                  ),
                  article_publish_timestamp: parseTimestamp(
                    isChannel ? post.dateCreated : post.publishedAt
                  ),
                  article_publisher: isChannel
                    ? typeof post.userUpdated === 'string'
                      ? post.userUpdated
                      : ''
                    : typeof post.channelName === 'string'
                      ? post.channelName
                      : '',
                  is_sponsored: !!post.isSponsored,
                  is_recommendation: !!post.isRecommendation,
                  sponsored_by: post.sponsoredBy?.toString() ?? '',
                },
                response: {
                  status: 'success',
                  message: 'successfully removed from my list',
                },
              },
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (errors: any) {
            await postAnalyticsEvent({
              eventName: 'click_read_remove_list',
              entryPoint: 'Content',
              vertical: 'Digital Content - Read & Post Articles',
              eventParams: {
                screen: screenValue,
                content: {
                  article_id: post.id,
                  article_title: post.title,
                  article_description: htmlToPlainText(
                    post.formattedText ?? post.content ?? post.description ?? ''
                  ),
                  article_publish_timestamp: parseTimestamp(
                    isChannel ? post.dateCreated : post.publishedAt
                  ),
                  article_publisher: isChannel
                    ? typeof post.userUpdated === 'string'
                      ? post.userUpdated
                      : ''
                    : typeof post.channelName === 'string'
                      ? post.channelName
                      : '',
                  is_sponsored: !!post.isSponsored,
                  is_recommendation: !!post.isRecommendation,
                  sponsored_by: post.sponsoredBy?.toString() ?? '',
                },
                response: {
                  status: 'fail',
                  message: errors?.message ?? 'Error posting analytics event',
                },
              },
            });
            console.error('Removed List : Error posting analytics event:', errors);
          }
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    },
    [customerDetails.id, isPostInMyList, myList, isChannel, screenValue]
  );

  const refetchList = useCallback(async () => {
    // Only refetch if we have a valid msisdn
    if (msisdn) {
      await fetchCustomerDetails();
    }
  }, [fetchCustomerDetails, msisdn]);

  const sortMyList = useCallback((sortByValue?: SortBy) => {
    if (sortByValue === SortBy.AZ) {
      setMyList((prevList) => [...prevList].sort((a, b) => a.title.localeCompare(b.title)));
    }
  }, []);

  return (
    <MyListContext.Provider
      value={{
        isInList: isPostInMyList,
        manageMyList,
        error,
        setFilter: setFilterCategories,
        toggleSort: sortMyList,
        refetchList,
        setIsChannel: setIsChannelCallback,
        myList,
        setScreen: setScreenCallback,
      }}
    >
      {children}
    </MyListContext.Provider>
  );
};

export const useMyList = (): MyListContextValue => {
  const context = useContext(MyListContext);
  if (!context) {
    throw new Error('useMyList must be used within MyListContextProvider');
  }
  return context;
};
